import React, { useState, useRef, useEffect } from 'react';
import { useHealthSystem } from '../../context/AuthContext';
import { useTheme } from '../redux/hooks/useTheme';

/**
 * HealthcareSelector
 *
 * Displays the active health system's logo and name.
 * If the user has access to more than one health system, renders a dropdown
 * to switch the active context.
 *
 * Reads from and writes to AuthContext via the useHealthSystem hook.
 */
const HealthcareSelector = ({ className = '' }) => {
  const { activeHealthSystem, accessibleHealthSystems, setActiveHealthSystem } = useHealthSystem();
  const { theme } = useTheme();
  const isDark = theme.mode === 'dark';
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!activeHealthSystem) return null;

  const hasMultiple = accessibleHealthSystems.length > 1;

  const handleSelect = (hs) => {
    setActiveHealthSystem(hs);
    setOpen(false);
  };

  // Deterministic gradient based on system name — stable across re-renders
  const GRADIENTS = [
    'linear-gradient(135deg, #667eea, #764ba2)',
    'linear-gradient(135deg, #f093fb, #f5576c)',
    'linear-gradient(135deg, #4facfe, #00f2fe)',
    'linear-gradient(135deg, #43e97b, #38f9d7)',
    'linear-gradient(135deg, #fa709a, #fee140)',
    'linear-gradient(135deg, #a18cd1, #fbc2eb)',
    'linear-gradient(135deg, #fccb90, #d57eeb)',
    'linear-gradient(135deg, #a1c4fd, #c2e9fb)',
    'linear-gradient(135deg, #fd7743, #f7971e)',
    'linear-gradient(135deg, #11998e, #38ef7d)',
  ];

  const getGradient = (seed = '') => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    }
    return GRADIENTS[hash % GRADIENTS.length];
  };

  // Fallback: show initials when no logo URL is present
  const LogoOrInitials = ({ system, size = 32 }) => {
    const initials = (system.name || '?')
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();

    if (system.healthcare_logo_url) {
      return (
        <img
          src={system.healthcare_logo_url}
          alt={system.name}
          style={{ width: size, height: size, objectFit: 'contain', borderRadius: 4 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }

    return (
      <span
        className="text-white d-flex align-items-center justify-content-center fw-bold"
        style={{
          width: size,
          height: size,
          borderRadius: 4,
          fontSize: size * 0.4,
          flexShrink: 0,
          background: getGradient(system.name || system.id),
        }}
      >
        {initials}
      </span>
    );
  };

  return (
    <div className={`healthcare-selector d-flex align-items-center gap-2 ${className}`} ref={dropdownRef}>
      <LogoOrInitials system={activeHealthSystem} size={32} />

      {hasMultiple ? (
        <div className="dropdown">
          <button
            className={`btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1 ${isDark ? 'text-light' : 'text-body'}`}
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            title="Switch health system"
          >
            <span className="fw-semibold text-truncate" style={{ maxWidth: 160 }}>
              {activeHealthSystem.name}
            </span>
            <i className={`fas fa-chevron-${open ? 'up' : 'down'} ms-1`} style={{ fontSize: 10 }}></i>
          </button>

          {open && (
            <ul
              className={`dropdown-menu show shadow-sm ${isDark ? 'dropdown-menu-dark' : ''}`}
              style={{ maxHeight: 300, overflowY: 'auto', right: 0, left: 'auto' }}
            >
              {accessibleHealthSystems.map((hs) => (
                <li key={hs.id}>
                  <button
                    className={`dropdown-item d-flex align-items-center gap-2 py-2 ${hs.id === activeHealthSystem.id ? 'active' : ''}`}
                    onClick={() => handleSelect(hs)}
                  >
                    <LogoOrInitials system={hs} size={24} />
                    <span className="text-truncate">{hs.name}</span>
                    {hs.id === activeHealthSystem.id && (
                      <i className="fas fa-check ms-auto text-primary" style={{ fontSize: 12 }}></i>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <span className={`fw-semibold text-truncate ${isDark ? 'text-light' : ''}`} style={{ maxWidth: 180 }}>
          {activeHealthSystem.name}
        </span>
      )}
    </div>
  );
};

export default HealthcareSelector;
